// packages/report-components/src/ExecSummary.tsx
//
// The executive readout — the opening block of every report, and after
// the 2026-08-12 redesign (R8-delta of the report-templates plan) the
// page's actual headline. Four parts, in order:
//
//   1. eyebrow — "EXECUTIVE READOUT", small uppercase accent label.
//   2. thesis (optional) — the page headline. Display-serif, upright,
//      responsive down from ~44px. The one sentence answer to "what's
//      the state of this business?"
//   3. summary (optional) — a running paragraph of 3–5 sentences that
//      says what happened and what to do about it.
//   4. bullets — legacy supporting insights. Max 4 enforced at render.
//
// Why the thesis is upright rather than italic: it is no longer a
// pull-quote inside a document that already had a serif title — the
// header above it is now a slim utility band, so this line carries the
// page. Italic at 44px reads as ornament; upright reads as a headline.
//
// Structural rules:
//   - No box, no tinted background, no centred column. Left-aligned and
//     full width so it sets the page's left edge, which the KPI tiles
//     and the two-column band below then line up against.
//   - `thesis`, `summary` or `bullets` must be non-empty; all absent →
//     renders null (caller keeps layout stable).
//
// Backwards compat: pre-redesign reports pass bullets only — they render
// under the eyebrow without a headline. Reports passing `thesis` keep
// working and simply gain the larger treatment.
//
// Typography unit — `cqi`, not `vw`: the report's page is the
// `.verity-sheet` container (1024px max, 64px inline padding → 896px
// CONTENT box, so 1cqi = 8.96px at full width), NOT the iframe viewport.
// Keying the headline to the viewport meant opening the iterate panel
// narrowed the report without changing its headline size. THE DEGRADATION
// IS RELIED ON: where no ancestor declares `container-type` — email (no
// sheet in its DOM) and print (the sheet sets `container-type: normal`
// in shell.css's print block) — `cqi` resolves against the small viewport,
// i.e. exactly the viewport-keyed behaviour those two surfaces have today.
// Print additionally pins explicit `!important` clamps in components.css.

import * as React from "react";

import { usePageDensity } from "./PageDensityContext";
import { PageEyebrow } from "./pageFrameParts";
import { pageLines, useTruncatedFlag } from "./pageClamp";

export type ExecSummaryProps = {
  /** The page headline — one sentence, display-serif, dominant. */
  thesis?: string;
  /** Running paragraph under the headline: what happened, why, and the
   * call to action. Rendered as a narrative slot (refreshable). */
  summary?: string;
  /** 2–4 supporting insight sentences. More than 4 are dropped. Legacy
   * surface — new reports prefer `summary`. */
  bullets?: string[];
  /** Optional eyebrow label — defaults to "Executive readout". */
  heading?: string;
  /** ON A PAGE: how many lines the server budgeted for the thesis and for the
   * summary (`layout._page_line_allocation`; the adapter passes them). Each
   * part is clamped to its count so the drawn height is the budgeted height.
   * Ignored off a page. */
  thesisLines?: number;
  summaryLines?: number;
  /** ON A REPORT PAGE only: the type the SERVER set the thesis in for this
   * cell (`layout.page_thesis_type`). A page's opening reading is a headline
   * and is set far larger than a thesis in a narrow cell can be, so the size
   * is per cell rather than one number in the density.
   *
   * Absent = the density's own `thesis_font_px` / `thesis_line_px`. That is
   * not a fallback for tidiness: a bundle compiled before these props existed
   * carries `thesisLines` priced at the density's line height, and drawing it
   * larger would clip text the server budgeted correctly. */
  thesisFontPx?: number;
  thesisLinePx?: number;
  /** ON A REPORT PAGE only: air above the eyebrow, in px
   * (`layout.PAGE_DISPLAY_LEAD_IN_PX`, passed by the adapter for the `thesis`
   * kind). A page's opening headline sits under the header band's hairline
   * with nothing between them, measured on paper on 2026-09-29; this is that
   * gap. The server PRICES the cell with it, so it is never the reason a
   * headline clips.
   *
   * Absent = 0, which is what every bundle compiled before it carries and
   * what an executive summary passes: it opens its cell with its eyebrow. */
  leadInPx?: number;
};

const MAX_BULLETS = 4;

export function ExecSummary({
  thesis,
  summary,
  bullets,
  heading = "Executive readout",
  thesisLines,
  summaryLines,
  thesisFontPx,
  thesisLinePx,
  leadInPx,
}: ExecSummaryProps): React.ReactElement | null {
  const density = usePageDensity();
  const thesisRef = useTruncatedFlag<HTMLParagraphElement>(`${thesis}|${thesisLines}`);
  const summaryRef = useTruncatedFlag<HTMLParagraphElement>(`${summary}|${summaryLines}`);
  const safeBullets = Array.isArray(bullets) ? bullets.slice(0, MAX_BULLETS) : [];
  const hasThesis = typeof thesis === "string" && thesis.trim().length > 0;
  const hasSummary = typeof summary === "string" && summary.trim().length > 0;
  if (!hasThesis && !hasSummary && safeBullets.length === 0) {
    return null;
  }
  if (density) {
    // The page form. Every part is a whole number of fixed px lines, the same
    // numbers `layout.py` prices an executive summary with, and there are no
    // margins around the section: the cell is the box. No measure cap either:
    // the budget counted characters over the cell's full width.
    const tLines = Math.max(1, Math.min(density.thesis_max_lines, thesisLines ?? density.thesis_max_lines));
    const tFont = thesisFontPx && thesisFontPx > 0 ? thesisFontPx : density.thesis_font_px;
    const tLine = thesisLinePx && thesisLinePx > 0 ? thesisLinePx : density.thesis_line_px;
    const rest = hasSummary || safeBullets.length > 0;
    // `paddingTop` and not `marginTop`: the cell is the box and the section
    // has `margin: 0` for that reason. A margin here would also collapse
    // through the section's top edge, which is the one behaviour that would
    // make the drawn height disagree with the priced one.
    const lead = typeof leadInPx === "number" && leadInPx > 0 ? leadInPx : 0;
    return (
      <section
        className="verity-exec-summary"
        data-page-prose="1"
        style={lead ? { margin: 0, paddingTop: lead } : { margin: 0 }}
      >
        <PageEyebrow density={density} className="verity-exec-summary__eyebrow" text={heading}>
          {heading}
        </PageEyebrow>
        {hasThesis ? (
          <p
            ref={thesisRef}
            className="verity-exec-summary__thesis"
            style={{
              margin: 0,
              marginBottom: rest ? density.thesis_gap_px : 0,
              fontFamily: "var(--verity-font-display, ui-serif, Georgia, serif)",
              fontSize: tFont,
              // A headline is set in the regular weight of the display face;
              // at 44 px the semibold of a small thesis reads as shouting.
              fontWeight: tFont > density.thesis_font_px ? 400 : 600,
              letterSpacing: "-0.02em",
              color: "var(--verity-color-ink, var(--verity-color-text, #111827))",
              ...pageLines(tLines, tLine),
            }}
          >
            {thesis}
          </p>
        ) : null}
        {hasSummary ? (
          <p
            ref={summaryRef}
            className="verity-exec-summary__summary"
            style={{
              margin: 0,
              fontSize: density.prose_font_px,
              // The standfirst under a headline is set grey, as in the
              // reference this page form is drawn from. The LEADING stays the
              // prose leading: the server prices these lines at
              // `PAGE_PROSE_LINE_PX`, and a component that draws them taller
              // than the budget is exactly the clipping this page density
              // exists to prevent.
              color: "var(--verity-color-text-muted, #6b7280)",
              ...(summaryLines && summaryLines > 0
                ? pageLines(summaryLines, density.prose_line_px, { fixed: false })
                : { lineHeight: `${density.prose_line_px}px` }),
            }}
          >
            {summary}
          </p>
        ) : null}
        {safeBullets.length > 0 ? (
          <ul
            style={{
              margin: 0,
              marginTop: hasSummary ? density.bullets_gap_px : 0,
              paddingLeft: 16,
              color: "var(--verity-color-text, #111827)",
              fontSize: density.prose_font_px,
            }}
          >
            {safeBullets.map((bullet, i) => (
              <li
                key={i}
                data-page-bullet="1"
                style={{
                  height: density.prose_line_px,
                  lineHeight: `${density.prose_line_px}px`,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {bullet}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    );
  }
  return (
    <section
      className="verity-exec-summary"
      style={{
        marginTop: "var(--verity-space-lg, 24px)",
        // 64 matches the document's inter-section rhythm
        // (`.verity-section + .verity-section { margin-top: 64px }`). When
        // the summary sits inside the first section the two margins
        // COLLAPSE to 64 rather than stack to 128 (.verity-section has no
        // padding/border to block collapsing); when it renders top-level
        // above the sections it provides the same 64 itself — uniform
        // rhythm in both bundle shapes.
        marginBottom: 64,
      }}
    >
      <h2
        className="verity-exec-summary__eyebrow"
        style={{
          margin: 0,
          marginBottom: hasThesis ? 12 : 10,
          fontSize: "var(--verity-font-size-xs, 12px)",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "var(--verity-color-primary, #3455D3)",
        }}
      >
        {heading}
      </h2>
      {hasThesis ? (
        <p
          className="verity-exec-summary__thesis"
          style={{
            margin: 0,
            marginBottom: hasSummary || safeBullets.length > 0 ? 16 : 0,
            fontFamily:
              "var(--verity-font-display, ui-serif, Georgia, serif)",
            // Sheet-relative headline: the report sheet is 1024px max with
            // 64px inline padding, so the content box is 896px and the
            // container unit is keyed to that box (1cqi = 8.96px). 3.4cqi
            // reaches ~30.5px at full sheet width, just under the 32px
            // ceiling, so the headline is effectively at full size on a
            // full-width page and shrinks with the sheet — not with the
            // iframe. The 24px floor is what a 640px email column gets.
            fontSize: "clamp(24px, 3.4cqi, 32px)",
            lineHeight: 1.18,
            fontWeight: 600,
            // Editorial ink — brighter than body in dark mode so the
            // serif thesis stays legible.
            color: "var(--verity-color-ink, var(--verity-color-text, #111827))",
            letterSpacing: "-0.02em",
            // Display measure — the same token every other lede-sized
            // block uses. At this smaller headline size a `ch` is closer
            // to the body `ch`, so 62ch now binds inside the sheet: the
            // headline wraps before it reaches the full 896px column.
            // That's intended — a headline that no longer runs the full
            // width is the point of scaling it down, not a regression of
            // the "sets the page's left edge, full width" alignment above,
            // which is about the left edge, not the line length.
            maxWidth: "var(--verity-measure-lede, 62ch)",
          }}
        >
          {thesis}
        </p>
      ) : null}
      {hasSummary ? (
        <p
          className="verity-exec-summary__summary"
          style={{
            margin: 0,
            marginBottom: safeBullets.length > 0 ? 16 : 0,
            fontSize: "var(--verity-font-size-md, 15px)",
            lineHeight: "var(--verity-line-height-relaxed, 1.65)",
            color: "var(--verity-color-text, #111827)",
            maxWidth: "var(--verity-measure-prose, 72ch)",
          }}
        >
          {summary}
        </p>
      ) : null}
      {safeBullets.length > 0 ? (
        <ul
          style={{
            margin: 0,
            paddingLeft: 24,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            color: "var(--verity-color-text, #111827)",
            fontSize: "var(--verity-font-size-md, 15px)",
            lineHeight: "var(--verity-line-height-normal, 1.5)",
          }}
        >
          {safeBullets.map((bullet, i) => (
            <li key={i}>{bullet}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
